import {
  getBasicKeys,
  getActionKeys,
  getEfficiencyKeys,
  getToolKeys,
  getMultimediaKeys,
  getSystemEditKeys,
  getWindowKeys,
  modifierKey,
  normalKey,
} from "@bytech/web-sdk-machine";
import {
  KEYCODE_MAP_BEIYING,
  MEDIA_KEYS_BEIYING,
  MOUSE_KEYS_BEIYING,
} from "@eweadn-qiankun/shared-components";

export const BEIYING_SDK_KEY_LIBRARY_STORAGE_KEY =
  "BEIYING_SDK_KEY_LIBRARY_916";

/** X98(8815 / web-sdk-machine) 键表缓存，与 916 分离避免混用 */
export const BEIYING_SDK_KEY_LIBRARY_8815_STORAGE_KEY =
  "BEIYING_SDK_KEY_LIBRARY_8815";

/** 8815 宏槽位与层、pos 的本地镜像（设备 getKeyboardKeys 常不带 type:M 时仍用于 UI 回显） */
export const BEIYING_8815_MACRO_BINDING_STORAGE_KEY =
  "BEIYING_8815_MACRO_BINDING_BY_LAYER";

type KeyItem = {
  name?: string;
  value?: string | number;
  icon?: string;
};

type EnrichedKeyItem = KeyItem & {
  numericValue?: number;
  keycode?: number;
  xsKeycode?: number;
  id?: number;
};

type ValueKeyInfo = {
  numericValue: number;
  keycode: number;
  xsKeycode?: number;
  id?: number;
};

export interface BeiyingSdkKeyLibrary {
  board: 916 | 8815;
  updatedAt: number;
  basicKeys: EnrichedKeyItem[];
  actionKeys: EnrichedKeyItem[];
  efficiencyKeys: EnrichedKeyItem[];
  toolKeys: EnrichedKeyItem[];
  multimediaKeys: EnrichedKeyItem[];
  systemEditKeys: EnrichedKeyItem[];
  windowKeys: EnrichedKeyItem[];
  modifierKey: EnrichedKeyItem[];
  normalKey: EnrichedKeyItem[];
  valueToNumberMap: Record<string, number>;
  valueToKeyInfoMap: Record<string, ValueKeyInfo>;
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const v = value.trim();
    if (!v) return undefined;
    if (/^0x[0-9a-f]+$/i.test(v)) {
      const parsedHex = Number.parseInt(v, 16);
      return Number.isFinite(parsedHex) ? parsedHex : undefined;
    }
    const parsedNum = Number(v);
    return Number.isFinite(parsedNum) ? parsedNum : undefined;
  }
  return undefined;
};

const mapToKeyInfo = (
  numericValue: number
): Omit<ValueKeyInfo, "numericValue"> => {
  const fromKeyboard = KEYCODE_MAP_BEIYING.find(
    (k) => k.keycode === numericValue || k.xsKeycode === numericValue
  );
  if (fromKeyboard) {
    return {
      keycode: fromKeyboard.keycode,
      xsKeycode: fromKeyboard.xsKeycode,
      id: fromKeyboard.id,
    };
  }
  const fromMedia = MEDIA_KEYS_BEIYING.find(
    (k) => k.keycode === numericValue || k.xsKeycode === numericValue
  );
  if (fromMedia) {
    return {
      keycode: fromMedia.keycode,
      xsKeycode: fromMedia.xsKeycode,
    };
  }
  const fromMouse = MOUSE_KEYS_BEIYING.find(
    (k) => k.keycode === numericValue || k.xsKeycode === numericValue
  );
  if (fromMouse) {
    return {
      keycode: fromMouse.keycode,
      xsKeycode: fromMouse.xsKeycode,
    };
  }
  return { keycode: numericValue };
};

const enrichKeyItems = (items: KeyItem[]): EnrichedKeyItem[] => {
  return items.map((item) => {
    const numericValue = toNumber(item.value);
    if (numericValue === undefined) return { ...item };
    const info = mapToKeyInfo(numericValue);
    return {
      ...item,
      numericValue,
      keycode: info.keycode,
      xsKeycode: info.xsKeycode,
      id: info.id,
    };
  });
};

const buildValueMaps = (
  groups: EnrichedKeyItem[][]
): {
  valueToNumberMap: Record<string, number>;
  valueToKeyInfoMap: Record<string, ValueKeyInfo>;
} => {
  const map: Record<string, number> = {};
  const infoMap: Record<string, ValueKeyInfo> = {};
  groups.forEach((items) => {
    items.forEach((item) => {
      const raw = item?.value;
      const n = item.numericValue ?? toNumber(raw);
      if (n === undefined) return;
      const keyInfo = mapToKeyInfo(n);
      const valueInfo: ValueKeyInfo = {
        numericValue: n,
        keycode: keyInfo.keycode,
        xsKeycode: keyInfo.xsKeycode,
        id: keyInfo.id,
      };
      map[String(raw)] = n;
      map[String(n)] = n;
      map[`0x${n.toString(16)}`] = n;
      map[`0X${n.toString(16).toUpperCase()}`] = n;
      infoMap[String(raw)] = valueInfo;
      infoMap[String(n)] = valueInfo;
      infoMap[`0x${n.toString(16)}`] = valueInfo;
      infoMap[`0X${n.toString(16).toUpperCase()}`] = valueInfo;
    });
  });
  return { valueToNumberMap: map, valueToKeyInfoMap: infoMap };
};

function collectBeiyingSdkKeyLibraryForBoard(
  board: 916 | 8815
): BeiyingSdkKeyLibrary {
  const basicKeys = enrichKeyItems(getBasicKeys(board as any));
  const actionKeys = enrichKeyItems(getActionKeys(board as any));
  const efficiencyKeys = enrichKeyItems(getEfficiencyKeys(board as any));
  const toolKeys = enrichKeyItems(getToolKeys(board as any));
  const multimediaKeys = enrichKeyItems(getMultimediaKeys(board as any));
  const systemEditKeys = enrichKeyItems(getSystemEditKeys(board as any));
  const windowKeys = enrichKeyItems(getWindowKeys(board as any));
  const modifierKeys = enrichKeyItems(modifierKey as KeyItem[]);
  const normalKeys = enrichKeyItems(normalKey as KeyItem[]);

  const { valueToNumberMap, valueToKeyInfoMap } = buildValueMaps([
    basicKeys,
    actionKeys,
    efficiencyKeys,
    toolKeys,
    multimediaKeys,
    systemEditKeys,
    windowKeys,
    modifierKeys,
    normalKeys,
  ]);

  return {
    board,
    updatedAt: Date.now(),
    basicKeys,
    actionKeys,
    efficiencyKeys,
    toolKeys,
    multimediaKeys,
    systemEditKeys,
    windowKeys,
    modifierKey: modifierKeys,
    normalKey: normalKeys,
    valueToNumberMap,
    valueToKeyInfoMap,
  };
}

export const collectBeiyingSdkKeyLibrary = (): BeiyingSdkKeyLibrary =>
  collectBeiyingSdkKeyLibraryForBoard(916);

export const syncBeiyingSdkKeyLibrary = (): BeiyingSdkKeyLibrary | null => {
  if (typeof window === "undefined") return null;
  try {
    const payload = collectBeiyingSdkKeyLibrary();
    localStorage.setItem(
      BEIYING_SDK_KEY_LIBRARY_STORAGE_KEY,
      JSON.stringify(payload)
    );
    return payload;
  } catch (error) {
    console.warn("[beiyingSdkKeyLibrary] sync failed:", error);
    return null;
  }
};

/** X98(8815) 连接成功后调用，写入 BEIYING_SDK_KEY_LIBRARY_8815 */
export const syncBeiying8815SdkKeyLibrary = (): BeiyingSdkKeyLibrary | null => {
  if (typeof window === "undefined") return null;
  try {
    const payload = collectBeiyingSdkKeyLibraryForBoard(8815);
    localStorage.setItem(
      BEIYING_SDK_KEY_LIBRARY_8815_STORAGE_KEY,
      JSON.stringify(payload)
    );
    return payload;
  } catch (error) {
    console.warn("[beiyingSdkKeyLibrary] 8815 sync failed:", error);
    return null;
  }
};
